import { logEvent } from "firebase/analytics";
import { initFirebaseAnalytics, getFirebaseAnalyticsInstance } from "./firebase-client.js";
import {
  sanitizeAnalyticsParams,
  analyticsParamsUseOnlyStringsAndIntegers,
} from "./sanitize-params.js";

export { sanitizeAnalyticsParams, analyticsParamsUseOnlyStringsAndIntegers };

/**
 * @typedef {Object} AnalyticsTrackerOptions
 * @property {() => Record<string, unknown>} [getStatsSnapshot]
 * @property {boolean} [enabled]
 * @property {() => Promise<import("firebase/analytics").Analytics | null>} [initAnalytics]
 * @property {(name: string, params: Record<string, unknown>) => void} [logEventFn]
 */

export class AnalyticsTracker {
  /**
   * @param {AnalyticsTrackerOptions} [options]
   */
  constructor(options) {
    options = options || {};
    this.enabled = options.enabled !== false;
    this.getStatsSnapshot = typeof options.getStatsSnapshot === "function"
      ? options.getStatsSnapshot
      : function () { return {}; };
    this.initAnalytics = typeof options.initAnalytics === "function"
      ? options.initAnalytics
      : initFirebaseAnalytics;
    this.logEventFn = typeof options.logEventFn === "function"
      ? options.logEventFn
      : function (analytics, name, params) {
        logEvent(analytics, name, params);
      };
    /** @type {Array<{ name: string, params: Record<string, unknown> }>} */
    this._queue = [];
    this._initializing = false;
    this._ready = false;
  }

  init() {
    if (!this.enabled || this._initializing || this._ready) return;
    this._initializing = true;
    var self = this;
    Promise.resolve(this.initAnalytics())
      .then(function (analytics) {
        self._analytics = analytics;
        self._ready = true;
        self._flushQueue();
      })
      .catch(function (err) {
        console.warn("[analytics] tracker init failed:", err);
        self._ready = true;
      })
      .finally(function () {
        self._initializing = false;
      });
  }

  /**
   * @param {string} eventName
   * @param {Record<string, unknown>} [extraParams]
   */
  track(eventName, extraParams) {
    if (!this.enabled || !eventName) return;
    if (!this._ready && !this._initializing) this.init();
    // The shared instance also covers Analytics started later (store app consent granted).
    var analytics = this.analytics;
    if (analytics) {
      this.logEventFn(analytics, eventName, this._buildPayload(extraParams));
    } else if (!this._ready) {
      this._queue.push({ name: eventName, extraParams: extraParams || {} });
    }
  }

  /**
   * @param {Record<string, unknown>} [extraParams]
   * @returns {Record<string, string | number>}
   */
  _buildPayload(extraParams) {
    var stats = {};
    try {
      stats = this.getStatsSnapshot() || {};
    } catch (e) {
      console.warn("[analytics] getStatsSnapshot failed:", e);
    }
    return sanitizeAnalyticsParams(Object.assign({}, stats, extraParams || {}));
  }

  _flushQueue() {
    if (!this._analytics) {
      this._queue = [];
      return;
    }
    while (this._queue.length > 0) {
      var item = this._queue.shift();
      if (!item) break;
      this.logEventFn(this._analytics, item.name, this._buildPayload(item.extraParams));
    }
  }

  /** @returns {import("firebase/analytics").Analytics | null | undefined} */
  get analytics() {
    return this._analytics || getFirebaseAnalyticsInstance();
  }
}

/**
 * @param {AnalyticsTrackerOptions} [options]
 * @returns {AnalyticsTracker}
 */
export function createAnalyticsTracker(options) {
  return new AnalyticsTracker(options);
}
